export const RESPONSIVE_DIM_KEYS = [
  "dim_1024*768",
  "dim_2752*2064",
  "dim_2388*1668",
  "dim_2048*1536",
  "dim_1280*720",
  "dim_1920*1080",
];

const styleManagerKeyToGroup = {
  value: "transform",
  "text-shadow": "text-shadow",
  transition: "transition",
  "box-shadow": "box-shadow",
  background: "background",
};

const PREFIX_GROUP_IDS = ["text-shadow", "transition", "box-shadow"];

const BACKGROUND_KEYS = [
  "background-image",
  "background-repeat",
  "background-position",
  "background-attachment",
  "background-size",
];

const TRANSFORM_FN_KEYS = new Set([
  "scaleX",
  "scaleY",
  "scaleZ",
  "rotateX",
  "rotateY",
  "rotateZ",
  "translateX",
  "translateY",
  "translateZ",
  "skewX",
  "skewY",
  "perspective",
  "matrix",
  "matrix3d",
]);

function nonemptyStyle(elemStyle, key) {
  if (!elemStyle || !key) return false;
  const v = elemStyle[key];
  return v != null && !(typeof v === "string" && v.trim() === "");
}

/** Remove stored row keys for one responsive group (`text-shadow`, `transition`, `box-shadow`, `background`, `transform`). */
function removeRowByGroupId(row, groupId) {
  if (!row || typeof row !== "object" || !groupId) return;

  if (PREFIX_GROUP_IDS.includes(groupId)) {
    const p = `${groupId}-`;
    Object.keys(row).forEach((k) => {
      if (k !== "id" && (k === groupId || k.startsWith(p))) delete row[k];
    });
    return;
  }

  if (groupId === "background") {
    const set = new Set(BACKGROUND_KEYS);
    Object.keys(row).forEach((k) => {
      if (k !== "id" && set.has(k)) delete row[k];
    });
    return;
  }

  if (groupId === "transform") {
    Object.keys(row).forEach((k) => {
      if (k === "id") return;
      if (k === "transform" || k === "value" || k === "name" || k.startsWith("transform-") || TRANSFORM_FN_KEYS.has(k)) {
        delete row[k];
      }
    });
  }
}

export function isResponsiveStyleStoragePropKey(propKey) {
  return propKey != null && Object.prototype.hasOwnProperty.call(styleManagerKeyToGroup, propKey);
}

export function stripResponsiveStyleGroupFromCssRow(row, styleManagerPropKey) {
  const gid = styleManagerKeyToGroup[styleManagerPropKey];
  if (gid) removeRowByGroupId(row, gid);
}

// Merge per-dimension cssObj after load: drop row keys when the component no longer has that style.
export function filterCssObjForPerDimension(elemStyle, row) {
  const { id: _rowId, ...rest } = row;
  const out = { ...rest };

  for (const base of PREFIX_GROUP_IDS) {
    let present = nonemptyStyle(elemStyle, base);
    if (!present) {
      const p = `${base}-`;
      for (const k of Object.keys(elemStyle || {})) {
        if (k.startsWith(p) && nonemptyStyle(elemStyle, k)) {
          present = true;
          break;
        }
      }
    }
    if (!present) removeRowByGroupId(out, base);
  }

  const img = elemStyle?.["background-image"];
  const bgPresent =
    nonemptyStyle(elemStyle, "background-color") ||
    (img != null && String(img).trim() !== "" && String(img).trim() !== "none") ||
    BACKGROUND_KEYS.filter((k) => k !== "background-image").some((k) => nonemptyStyle(elemStyle, k));
  if (!bgPresent) removeRowByGroupId(out, "background");

  const t = elemStyle?.transform;
  const transformPresent = t != null && String(t).trim() !== "" && String(t).trim() !== "none";
  if (!transformPresent) removeRowByGroupId(out, "transform");

  return out;
}

export function isNonDefaultResponsiveDimension(defaultRatio, dim) {
  return (
    !!defaultRatio &&
    !!dim &&
    dim !== defaultRatio &&
    RESPONSIVE_DIM_KEYS.includes(dim)
  );
}

export function getCssStorageId(component) {
  if (!component) return "";
  const attrId = component.getAttributes?.()?.id;
  if (attrId) return String(attrId);
  if (component.ccid != null) return String(component.ccid);
  return "";
}

export function resolveStyleManagerPropKey(smProperty) {
  if (!smProperty) return "";
  const fromAttr = smProperty.attributes?.property;
  if (fromAttr) return fromAttr;
  if (typeof smProperty.get === "function") {
    const v = smProperty.get("property");
    if (v) return v;
  }
  if (typeof smProperty.getId === "function") {
    const gid = smProperty.getId();
    if (gid) return gid;
  }
  return smProperty.id || "";
}

export function getSelectedComponentsForCssSync(editor) {
  if (!editor) return [];
  const isCssSyncTarget = (m) =>
    m &&
    m.getStyle;
  const all =
    typeof editor.getSelectedAll === "function"
      ? editor.getSelectedAll()
      : [];
  const list = Array.isArray(all) && all.length ? all : [];
  if (list.length) return list.filter(isCssSyncTarget);
  const one = editor.getSelected?.();
  return isCssSyncTarget(one) ? [one] : [];
}

export function collectRemovalIdsSet(component) {
  const ids = new Set();
  const visit = (node) => {
    if (!node) return;
    if (node.ccid != null) ids.add(String(node.ccid));
    const aid = node.getAttributes?.()?.id;
    if (aid) ids.add(String(aid));
    const ch = node.components?.();
    if (ch && typeof ch.forEach === "function") ch.forEach(visit);
  };
  visit(component);
  return ids;
}

export function buildDimensionCssString(dim, rows, sortCSSObject = (obj) => Object.entries(obj)) {
  if (!dim || !Array.isArray(rows)) return "";
  let cssString = "";
  rows.forEach((obj) => {
    const sortedObj = sortCSSObject(obj);
    let idName = "";
    let properties = "";
    for (const [key, value] of sortedObj) {
      if (key === "id") {
        idName = "#" + obj[key];
      } else {
        properties += `${key}: ${value};`;
      }
    }
    if (idName) {
      cssString += `${idName} { ${properties} } `;
    }
  });
  const hasWrapperCSS = /#wrapper\s*\{[^}]*\}/i.test(cssString);
  if (!hasWrapperCSS && dim) {
    const sizePart = dim.replace("dim_", "");
    const [width, height] = sizePart.split("*");
    if (width && height) {
      cssString += ` #wrapper { width: ${width}px; height: ${height}px } `;
    }
  }

  if (dim === "dim_2752*2064") {
    cssString += ` body #wrapper { transform: scale(1) !important; } `;
  }

  if (dim === "dim_1280*720") {
    cssString += ` body #wrapper { transform: scale(1) !important; } `;
  }

  if (dim === "dim_1920*1080") {
    cssString += ` @media screen and (min-width: 1920px) and (max-width: 1922px) { body #wrapper { transform: scale(1) !important; } }`;
  }

  return cssString;
}

function styleRuleTargetsDeleted(style, idsSet) {
  if (!style || typeof style !== "object") return false;
  const sels = style.selectors;
  if (Array.isArray(sels) && sels.length) {
    const sel = sels[0];
    const name =
      typeof sel === "object" && sel
        ? sel.name
        : typeof sel === "string"
          ? sel
          : "";
    if (name) {
      const n = String(name).replace(/^#/, "");
      if (idsSet.has(n) || idsSet.has(String(name))) return true;
    }
  }
  if (style.selectorsAdd && typeof style.selectorsAdd === "string") {
    const add = style.selectorsAdd;
    for (const id of idsSet) {
      if (!id) continue;
      if (
        add === `#${id}` ||
        add.includes(`#${id}{`) ||
        add.includes(`#${id} {`)
      )
        return true;
    }
  }
  return false;
}

function readDefaultDimStyle(parsed, storageId) {
  if (!parsed || !storageId) return {};
  const out = {};
  let found = null;
  const walk = (comp) => {
    if (!comp || found) return;
    const cid = comp.ccid != null ? String(comp.ccid) : "";
    const aid = comp.attributes?.id != null ? String(comp.attributes.id) : "";
    if (cid === String(storageId) || aid === String(storageId)) {
      found = comp;
      return;
    }
    comp.components?.forEach?.(walk);
  };
  walk(parsed?.pages?.[0]?.frames?.[0]?.component);
  if (found?.style && typeof found.style === "object" && !Array.isArray(found.style)) {
    Object.assign(out, found.style);
  }
  const ids = new Set([String(storageId)]);
  parsed.styles?.forEach?.((rule) => {
    if (rule?.style && styleRuleTargetsDeleted(rule, ids)) {
      Object.assign(out, rule.style);
    }
  });
  return out;
}

export function getDefaultDimStyle(storageObjRef, defaultRatio, storageId) {
  try {
    const ed =
      storageObjRef?.current?.[defaultRatio]?.editorData ||
      storageObjRef?.current?.editorData;
    const out = ed ? readDefaultDimStyle(JSON.parse(ed), storageId) : {};
    const defaultCssRow = storageObjRef?.current?.[defaultRatio]?.cssObj?.find(
      (row) => String(row?.id) === String(storageId),
    );
    if (defaultCssRow && typeof defaultCssRow === "object") {
      const { id: _id, ...style } = defaultCssRow;
      Object.assign(out, style);
    }
    return out;
  } catch {
    return {};
  }
}

function cssValuesEqual(a, b) {
  const norm = (v) => (v == null ? "" : String(v).trim());
  return norm(a) === norm(b);
}

/** Non-default dim cssObj: keep only styles that differ from the default dimension. */
export function filterCssObjToDimensionOverrides(rows, storageObjRef, defaultRatio) {
  if (!Array.isArray(rows)) return [];
  if (!defaultRatio || !storageObjRef?.current) return rows;

  return rows.flatMap((row) => {
    if (!row || row.id == null) return [];
    const defaultStyle = getDefaultDimStyle(storageObjRef, defaultRatio, row.id);
    const out = { id: row.id };

    Object.keys(row).forEach((key) => {
      if (key === "id") return;
      if (!cssValuesEqual(row[key], defaultStyle[key])) {
        out[key] = row[key];
      }
    });

    return Object.keys(out).length > 1 ? [out] : [];
  });
}

export function getCssObjForDimensionSave(
  selectedItem,
  cssRefRows,
  storageObjRef,
  defaultRatio,
  storageRows,
) {
  if (!defaultRatio) return Array.isArray(cssRefRows) ? cssRefRows : [];

  const source =
    Array.isArray(cssRefRows) && cssRefRows.length > 0
      ? cssRefRows
      : Array.isArray(storageRows)
        ? storageRows
        : [];

  if (selectedItem === defaultRatio) return source;

  return filterCssObjToDimensionOverrides(source, storageObjRef, defaultRatio);
}

/** Remove one override key from a cssObj row (do not copy default-dim values into the row). */
export function clearCssObjOverrideFromRow(row, propKey) {
  if (!row || !propKey) return;
  if (isResponsiveStyleStoragePropKey(propKey)) {
    stripResponsiveStyleGroupFromCssRow(row, propKey);
  } else {
    delete row[propKey];
  }
}

/** Runtime cssObj for canvas (non-default dim): prefer in-memory cssRef, fall back to storage. */
export function resolveCssObjForDimension(cssRef, storageObjRef, ratio) {
  const defaultRatio = storageObjRef?.current?.default_ratio;
  const fromRef = cssRef?.current?.[ratio];
  const fromStorage = storageObjRef?.current?.[ratio]?.cssObj;
  let rows =
    Array.isArray(fromRef) && fromRef.length > 0
      ? fromRef
      : Array.isArray(fromStorage)
        ? fromStorage
        : [];
  if (defaultRatio && ratio && ratio !== defaultRatio && rows.length) {
    rows = filterCssObjToDimensionOverrides(rows, storageObjRef, defaultRatio);
  }
  return rows;
}

/** X on non-default dim: write default-dim value into cssObj instead of deleting. */
export function setCssRowPropToDefaultDim(row, propKey, defaultStyle) {
  if (!row || !propKey) return;
  if (isResponsiveStyleStoragePropKey(propKey)) {
    stripResponsiveStyleGroupFromCssRow(row, propKey);
    const gid = styleManagerKeyToGroup[propKey];
    Object.keys(defaultStyle || {}).forEach((k) => {
      if (!nonemptyStyle(defaultStyle, k)) return;
      if (gid === "background" && BACKGROUND_KEYS.includes(k)) row[k] = defaultStyle[k];
      else if (
        gid === "transform" &&
        (k === "transform" || k === "value" || k.startsWith("transform-") || TRANSFORM_FN_KEYS.has(k))
      ) {
        row[k] = defaultStyle[k];
      } else if (gid !== "background" && gid !== "transform" && (k === gid || k.startsWith(`${gid}-`))) {
        row[k] = defaultStyle[k];
      }
    });
  } else if (nonemptyStyle(defaultStyle, propKey)) {
    row[propKey] = defaultStyle[propKey];
  } else {
    delete row[propKey];
  }
}

function pruneComponentsArray(components, idsSet) {
  if (!Array.isArray(components)) return components;
  return components
    .filter((comp) => {
      if (!comp || typeof comp !== "object") return true;
      const cid = comp.ccid != null ? String(comp.ccid) : "";
      const aid =
        comp.attributes?.id != null ? String(comp.attributes.id) : "";
      if (cid && idsSet.has(cid)) return false;
      if (aid && idsSet.has(aid)) return false;
      return true;
    })
    .map((comp) => {
      if (comp.components) {
        return {
          ...comp,
          components: pruneComponentsArray(comp.components, idsSet),
        };
      }
      return comp;
    });
}

export function pruneEditorDataString(editorDataStr, idsSet) {
  if (!editorDataStr || typeof editorDataStr !== "string") return editorDataStr;
  try {
    const parsed = JSON.parse(editorDataStr);
    if (parsed != null && typeof parsed === "object") {
      if (Array.isArray(parsed.styles)) {
        parsed.styles = parsed.styles.filter(
          (s) => !styleRuleTargetsDeleted(s, idsSet),
        );
      }
      const root = parsed.pages?.[0]?.frames?.[0]?.component;
      if (root && Array.isArray(root.components)) {
        root.components = pruneComponentsArray(root.components, idsSet);
      }
    }
    return JSON.stringify(parsed);
  } catch {
    return editorDataStr;
  }
}
